import { onValue, ref, runTransaction } from "firebase/database";

export const getHeldLines = (order) => {
  // Mobile updates line_items in place; web saves lineitems. Older aliases can
  // remain in a node, so use the representation owned by the last writer.
  const lines = order?.updated_by && order.updated_by !== "web"
    ? order?.line_items ?? order?.lineitems
    : order?.lineitems ?? order?.line_items;
  return Array.isArray(lines) ? lines.filter((line) => Number(line?.quantity ?? line?.qty) > 0) : [];
};

export const hasHeldOrder = (order) =>
  getHeldLines(order).length > 0 ||
  Object.values(order?.products || {}).some((qty) => Number(qty) > 0);

// Products is an aggregate (including variants), not an additional set of lines.
export const getHeldBaseProducts = (products = {}, lines = []) => {
  const base = { ...products };
  lines.forEach((line) => {
    const addons = line.addon_ids || line.addons || [];
    if (
      !line.variantid &&
      !addons.length &&
      !line.is_loyalty_reward &&
      !line.is_weight_line
    ) return;
    const id = line.productid ?? line.product_id;
    base[id] = Math.max(0, Number(base[id] || 0) - Number(line.quantity ?? line.qty ?? 0));
  });
  return Object.fromEntries(Object.entries(base).filter(([, qty]) => Number(qty) > 0));
};

export const completeHeldLines = (order, catalog = []) => {
  const lines = getHeldLines(order).map((line) => ({
    ...line,
    productid: line.productid ?? line.product_id,
    quantity: Number(line.quantity ?? line.qty),
  }));
  const represented = {};
  lines.forEach((line) => {
    represented[line.productid] = (represented[line.productid] || 0) + line.quantity;
  });
  Object.entries(order?.products || {}).forEach(([id, qty]) => {
    const missing = Number(qty) - (represented[id] || 0);
    if (missing <= 0) return;
    const product = catalog.find((item) => String(item.id) === id);
    if (!product) throw new Error("Open both table bills once to load their products, then try again.");
    lines.push({
      productid: Number(id), variantid: null, addon_ids: [], quantity: missing,
      name: product.name || `Item ${id}`,
      price: Number(product.price || 0),
      priceaftertax: Number(product.priceaftertax ?? product.price ?? 0),
      taxedprice: Number(product.taxedprice ?? product.priceaftertax ?? product.price ?? 0),
      taxamount: Number(product.taxamount || 0),
      sgst: Number(product.sgst || 0), cgst: Number(product.cgst || 0),
    });
  });
  return lines;
};

export const mergeHeldOrders = (source, target, catalog = []) => {
  const lines = [...completeHeldLines(target, catalog), ...completeHeldLines(source, catalog)];
  const merged = new Map();
  lines.forEach((line) => {
    // Retain distinct prices, taxes, variants, add-ons and reward grants.
    const identity = JSON.stringify([
      line.productid, line.variantid || null,
      [...(line.addon_ids || line.addons || [])].map((a) => String(a?.id ?? a)).sort(),
      line.price, line.priceaftertax, line.taxedprice, line.taxamount, line.sgst, line.cgst,
      line.is_loyalty_reward || 0, line.loyalty_reward_grant_id || null,
      line.is_weight_line || 0, line.sold_weight_grams || null, line.base_unit_price || null,
    ]);
    const previous = merged.get(identity);
    merged.set(identity, previous ? { ...previous, quantity: previous.quantity + line.quantity } : line);
  });
  const lineitems = [...merged.values()];
  const products = {};
  lineitems.forEach((line) => { products[line.productid] = (products[line.productid] || 0) + line.quantity; });
  const timestamp = (value) => {
    const numeric = Number(value);
    return numeric > 1e12 ? numeric : numeric > 1e9 ? numeric * 1000 : Date.parse(value);
  };
  const durations = [source?.duration, target?.duration].filter((value) => value && Number.isFinite(timestamp(value)));
  const duration = durations.sort((a, b) => timestamp(a) - timestamp(b))[0] || target?.duration || source?.duration || null;
  return {
    ...target, products, lineitems, line_items: lineitems,
    amount: Math.round((Number(source?.amount || 0) + Number(target?.amount || 0)) * 100) / 100,
    duration, updated_by: "web", updated_at: Date.now(),
  };
};

const waitForConnection = (db) => new Promise((resolve, reject) => {
  let unsubscribe = () => {};
  const timer = setTimeout(() => {
    unsubscribe();
    reject(new Error("Connect to the internet to shift, swap or merge tables."));
  }, 5000);
  unsubscribe = onValue(ref(db, ".info/connected"), (snapshot) => {
    if (snapshot.val() !== true) return;
    clearTimeout(timer);
    Promise.resolve().then(() => unsubscribe());
    resolve();
  }, (error) => { clearTimeout(timer); reject(error); });
});

export async function transferHeldTable({ db, restaurantId, sourceKey, targetKey, mode, catalog = [] }) {
  if (!restaurantId || !sourceKey || !targetKey || sourceKey === targetKey || !["swap", "merge"].includes(mode)) {
    throw new Error("Select a different destination table.");
  }
  if (navigator.onLine === false) throw new Error("Connect to the internet to shift, swap or merge tables.");
  await waitForConnection(db);
  const restaurantRef = ref(db, `/restaurants/${restaurantId}`);
  const copyOrder = (order) => ({
    ...order, lineitems: getHeldLines(order), line_items: getHeldLines(order),
    updated_by: "web", updated_at: Date.now(),
  });
  let operationError = null;
  let targetWasOccupied = false;
  // The transaction always works from Firebase's latest state. If Firebase
  // retries it after another write, the operation is rebuilt from that state.
  const result = await runTransaction(restaurantRef, (current) => {
    // Firebase may initially call a transaction with an empty local cache. A
    // no-op return lets the server respond and retry with its current value.
    if (!current) return current;
    const source = current?.tables?.[sourceKey];
    const target = current?.tables?.[targetKey];
    const sourceWaiter = current?.tableswaiters?.[sourceKey];
    const targetWaiter = current?.tableswaiters?.[targetKey];
    if (!hasHeldOrder(source)) {
      operationError = "The source table has no synced running order. Open its bill and save it, then try again.";
      return current;
    }
    operationError = null;
    const targetHasOrder = hasHeldOrder(target);
    targetWasOccupied = targetHasOrder;
    const nextSource = mode === "swap" && targetHasOrder ? copyOrder(target) : null;
    const nextTarget = mode === "merge" && targetHasOrder
      ? mergeHeldOrders(source, target, catalog)
      : copyOrder(source);
    return {
      ...current,
      tables: {
        ...current.tables,
        // An explicit empty slot invalidates stale carts on other clients,
        // while an absent node can still represent a never-synced offline cart.
        [sourceKey]: nextSource || { products: {}, amount: 0, updated_by: "web", updated_at: Date.now(), transferred_to: targetKey },
        [targetKey]: nextTarget,
      },
      tableswaiters: {
        ...current.tableswaiters,
        [sourceKey]: nextSource ? (targetWaiter || null) : null,
        [targetKey]: mode === "merge" ? (targetWaiter || null) : (sourceWaiter || null),
      },
    };
  }, { applyLocally: false });
  if (operationError) throw new Error(operationError);
  if (!result.committed) throw new Error("Could not update the tables. Please try again.");
  return {
    ...result.snapshot.val(),
    _tableTransfer: { targetWasOccupied },
  };
}

const CART_PREFIXES = ["ordersitems1", "orderlines1", "ordersitems1_lines", "orderAmount1", "orderStartTime1", "orderWaiter1", "orderParty1"];
export const cacheHeldOrder = (key, order, waiter, storage = localStorage) => {
  CART_PREFIXES.forEach((prefix) => storage.removeItem(prefix + key));
  if (!hasHeldOrder(order)) return;
  const lines = getHeldLines(order);
  const products = { ...(order.products || {}) };
  // Line-only payloads from other clients must also hydrate in the web POS.
  if (!Object.keys(products).length) lines.forEach((line) => {
    const id = line.productid ?? line.product_id;
    products[id] = (products[id] || 0) + Number(line.quantity ?? line.qty);
  });
  storage.setItem("ordersitems1" + key, JSON.stringify(products));
  storage.setItem("orderlines1" + key, JSON.stringify(lines));
  storage.setItem("ordersitems1_lines" + key, JSON.stringify(lines));
  storage.setItem("orderAmount1" + key, String(order.amount || 0));
  if (order.duration) storage.setItem("orderStartTime1" + key, String(order.duration));
  const waiterId = order.waiterid || waiter?.waiterid;
  const partyId = order.party || waiter?.selectedpartyid;
  if (waiterId) storage.setItem("orderWaiter1" + key, String(waiterId));
  if (partyId) storage.setItem("orderParty1" + key, String(partyId));
};

export const transferKotCache = (sourceKey, targetKey, mode, storage = localStorage, targetOccupied = true) => {
  ["KOTData", "KOTBaseline", "KOTToken"].forEach((prefix) => {
    const source = storage.getItem(prefix + sourceKey);
    const target = targetOccupied ? storage.getItem(prefix + targetKey) : null;
    storage.removeItem(prefix + sourceKey);
    storage.removeItem(prefix + targetKey);
    if (mode === "swap") {
      if (target) storage.setItem(prefix + sourceKey, target);
      if (source) storage.setItem(prefix + targetKey, source);
    } else if (prefix === "KOTToken") {
      if (target || source) storage.setItem(prefix + targetKey, target || source);
    } else {
      const parse = (value) => { try { const list = JSON.parse(value); return Array.isArray(list) ? list : []; } catch { return []; } };
      storage.setItem(prefix + targetKey, JSON.stringify([...parse(target), ...parse(source)]));
    }
  });
};
